import { StepsControl } from "./StepsControls/StepsControls";
import { RecipeType } from "@/types/baseTypes";
import { Section } from "@/components/Global/Section/Section";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import styles from "./HeaderRecipe.module.scss";
import { customSanitize } from "@/utils/customSanitize";
import { useRouter } from "next/router";
interface HeaderRecipeProps {
  recipeDetails: RecipeType;
  steps: RecipeType["steps_portion_big"] | RecipeType["steps_portion_small"];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  shouldMuteAudio: boolean;
  setAudioMute: (mute: boolean) => void;
}
export const HeaderRecipe = ({
  recipeDetails,
  steps,
  currentStep,
  setCurrentStep,
  shouldMuteAudio,
  setAudioMute,
}: HeaderRecipeProps) => {
  const { getLabel, getIcon } = useGlobalContext();
  const router = useRouter();

  const closeIcon = (
    <ReactSVG
      src={getIcon("close")}
      wrapper={"svg"}
      key={"close"}
      width={20}
      height={20}
      aria-hidden
      beforeInjection={(svg) => {
        svg.setAttribute("alt", `Menu close Icon`);
        svg.setAttribute("aria-label", `Menu close Icon`);
      }}
    />
  );

  return (
    <Section
      disableColumns
      disableContainer
      className={` ${styles.header} has-text-centered py-0 px-0`}
    >
      {currentStep >= -3 && (
        <>
          <button
            type="button"
            className={styles["btn-mute"]}
            onClick={() => {
              setAudioMute(!shouldMuteAudio);
            }}
            aria-pressed={!shouldMuteAudio}
            aria-label={
              shouldMuteAudio
                ? getLabel("cooking_mode_mute", "Stummschalten")
                : getLabel("cooking_mode_unmute", "Stummschaltung aufheben")
            }
          >
            <ReactSVG
              width={22}
              height={22}
              src={shouldMuteAudio ? getIcon("audioOff") : getIcon("audioOn")}
              wrapper="svg"
              aria-hidden
              beforeInjection={(svg) => {
                svg.setAttribute("aria-hidden", "true");
                svg.setAttribute("focusable", "false");
                svg.removeAttribute("aria-label");
                svg.removeAttribute("alt");
              }}
            />
          </button>
          <button
            className={styles["btn-close"]}
            type="button"
            aria-label={getLabel(
              "cooking_mode_end_button_label",
              "Kochmodus schließen",
            )}
            onClick={() => {
              router.push(`/rezepte/${recipeDetails.rezept_id}`);
            }}
          >
            {closeIcon}
          </button>
        </>
      )}

      <div className="column is-12-tablet py-0 px-0">
        <div className={styles.logo}>
          <p className={styles.text + " pb-0"}>{getLabel("cooking_mode")}</p>
          <ReactSVG
            width={25}
            height={25}
            src={getIcon("circleCookingMode")}
            wrapper="svg"
            aria-hidden
            beforeInjection={(svg) => {
              svg.setAttribute("aria-hidden", "true");
              svg.setAttribute("focusable", "false");
              svg.removeAttribute("aria-label");
              svg.removeAttribute("alt");
            }}
          />
        </div>
        <nav aria-label="Schritte">
          <progress
            id="progressbar"
            value={Math.max(0, Math.min(currentStep + 1, steps.length))}
            max={steps.length}
            aria-label={getLabel(
              "aria_label_cooking_mode_progress_bar",
              "Fortschritt",
            )}
            aria-describedby="progress-status"
            style={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ["--steps" as any]: String(steps.length),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ["--gap" as any]: "2px",
            }}
          />

          <span
            id="progress-status"
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clip: "rect(0,0,0,0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            {currentStep >= 0 && currentStep < steps.length
              ? getLabel(
                  "aria_label_cooking_mode_steps",
                  'Schritt "${i}" von "${total}"',
                )
                  .replace(
                    /["`]?(\$\{i\}|\{i\})["`]?/g,
                    String(currentStep + 1),
                  )
                  .replace(
                    /["`]?(\$\{total\}|\{total\})["`]?/g,
                    String(steps.length),
                  )
              : currentStep === steps.length
                ? getLabel("done", "Geschafft")
                : currentStep === -2
                  ? getLabel("ingredients_list", "Zutatenliste")
                  : currentStep === -1
                    ? getLabel(
                        "cooking_mode_utensils_title",
                        "Das brauchst du:",
                      )
                    : getLabel("cooking_mode", "Kochmodus")}
          </span>

          {currentStep >= -2 && (
            <StepsControl
              steps={steps}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
            />
          )}
        </nav>

        <h1
          className={`${styles["recipe-title"]} `}
          dangerouslySetInnerHTML={{
            __html: customSanitize(recipeDetails?.rezept_titel),
          }}
        />
      </div>
    </Section>
  );
};
export default HeaderRecipe;
